import React from "react";
import { Controller, useFormContext } from "react-hook-form";

import { EnvironmentConfig } from "src/aiNode/editorComponents/EnvironmentConfig";
import { AiNodeV2Form } from "src/aiNode/types";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { Divider } from "src/base-components/Divider";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Select } from "src/base-components/Select";
import { Switch } from "src/base-components/Switch";
import { AiNodeV2ResponseFormatConfigTypeEnum } from "src/clients/flow-api";
import { Tooltip } from "src/design-system/Tooltip";

type AdvancedConfigPropsT = {
  immutable: boolean;
};

type InferenceConfigPropsT = AdvancedConfigPropsT & {
  isOutputStructured: boolean;
};

const SYSTEM_PROMPT_PLACEHOLDER =
  "e.g. You are a specialized credit risk analyst working within a regulated financial institution. Your role is to provide structured analysis of credit applications.";

const STOP_SEQUENCE_DESCRIPTION =
  "Specify text patterns that signal the AI to stop generating output. You can add multiple sequences.";

const TEMPERATURE_DESCRIPTION =
  "Adjust the level of randomness in the AI's output. Lower values (closer to 0) create precise and consistent responses, while higher values (closer to 1) make them more creative and varied.";

const MAX_TOKENS_DESCRIPTION =
  "Limit the length of the AI's response. Higher values allow for longer outputs but may increase response times.";

const SystemPromptConfig: React.FC<AdvancedConfigPropsT> = ({ immutable }) => {
  const { watch, control } = useFormContext<AiNodeV2Form>();
  const systemPromptEnabled = watch("prompts.system_prompt.active");

  return (
    <div data-loc="ai-node-config-system-prompt">
      <div className="items-top flex justify-between gap-x-2">
        <div>
          <h3 className="text-gray-800 font-inter-semibold-13px">
            Add system prompt
          </h3>
          <span className="text-gray-500 font-inter-normal-12px">
            Provide additional context or instructions to guide how the AI
            approaches the task.
          </span>
        </div>
        <div>
          <Controller
            control={control}
            name="prompts.system_prompt.active"
            render={({ field: controlledField }) => (
              <Switch
                dataLoc="ai-node-config-system-prompt-toggle"
                disabled={immutable}
                enabled={controlledField.value}
                onChange={() => {
                  controlledField.onChange(!controlledField.value);
                }}
              />
            )}
          />
        </div>
      </div>
      {systemPromptEnabled && (
        <div>
          <Controller
            control={control}
            name="prompts.system_prompt.expression"
            render={({ field }) => (
              <div className="mt-2 h-80">
                <CodeEditor
                  disabled={immutable}
                  language="prompt"
                  placeholder={SYSTEM_PROMPT_PLACEHOLDER}
                  value={field.value ?? ""}
                  lineWrapping
                  onChange={field.onChange}
                />
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

const StopSequenceConfig: React.FC<InferenceConfigPropsT> = ({
  immutable,
  isOutputStructured,
}) => {
  const { control } = useFormContext<AiNodeV2Form>();

  // TODO: INT-4990 Implement proper stop sequence input
  const stopSequenceOptions = ["<|end|>", "<|end_of_text|>", "test"].map(
    (option) => ({
      key: option,
      value: option,
    }),
  );

  return (
    <div data-loc="ai-node-config-stop-sequence">
      <FormItem>
        <FormItem.Label description={STOP_SEQUENCE_DESCRIPTION}>
          Stop sequence
        </FormItem.Label>
        <Controller
          control={control}
          name="inference.stop_sequences"
          render={({ field: controlledField }) => (
            <Tooltip
              disabled={!isOutputStructured}
              title="You cannot configure Stop Sequence if Structured output is enabled"
              asChild
            >
              <div className="w-full">
                <Select
                  disabled={immutable || isOutputStructured}
                  options={stopSequenceOptions}
                  placeholder="e.g. '<|endoftext|>'"
                  value={
                    isOutputStructured ? [] : (controlledField.value ?? [])
                  }
                  multiple
                  onChange={controlledField.onChange}
                />
              </div>
            </Tooltip>
          )}
        />
      </FormItem>
    </div>
  );
};

const MaxTokensConfig: React.FC<InferenceConfigPropsT> = ({
  immutable,
  isOutputStructured,
}) => {
  const { control } = useFormContext<AiNodeV2Form>();

  return (
    <div data-loc="ai-node-config-max-tokens">
      <FormItem>
        <FormItem.Label description={MAX_TOKENS_DESCRIPTION}>
          Max tokens
        </FormItem.Label>
        <Controller
          control={control}
          name="inference.max_tokens"
          render={({ field: controlledField }) => (
            <Tooltip
              disabled={!isOutputStructured}
              title="You cannot configure Max Tokens if Structured output is enabled"
              asChild
            >
              <Input
                // TODO: Form errors
                disabled={immutable || isOutputStructured}
                placeholder="e.g. 1000"
                type="number"
                value={isOutputStructured ? "" : controlledField.value}
                fullWidth
                onChange={(event) =>
                  controlledField.onChange(parseInt(event.target.value))
                }
              />
            </Tooltip>
          )}
        />
      </FormItem>
    </div>
  );
};

const InferenceConfig: React.FC<InferenceConfigPropsT> = ({
  immutable,
  isOutputStructured,
}) => {
  const { register } = useFormContext<AiNodeV2Form>();

  return (
    <div className="flex w-full flex-col gap-y-4">
      <SystemPromptConfig immutable={immutable} />
      <StopSequenceConfig
        immutable={immutable}
        isOutputStructured={isOutputStructured}
      />
      <MaxTokensConfig
        immutable={immutable}
        isOutputStructured={isOutputStructured}
      />
      <div data-loc="ai-node-config-temperature">
        <FormItem>
          <FormItem.Label description={TEMPERATURE_DESCRIPTION}>
            Temperature
          </FormItem.Label>
          <Input
            // TODO: Form errors
            disabled={immutable}
            placeholder="e.g. 0.5"
            type="number"
            fullWidth
            {...register("inference.temperature", {
              valueAsNumber: true,
              min: 0,
              max: 1,
            })}
          />
        </FormItem>
      </div>
    </div>
  );
};

const EnvConfig: React.FC<AdvancedConfigPropsT> = ({ immutable }) => {
  return <EnvironmentConfig disabled={immutable} />;
};

const ExecutionConfig: React.FC<AdvancedConfigPropsT> = ({ immutable }) => {
  const { control, register, getValues } = useFormContext<AiNodeV2Form>();
  const isTimeoutActive = getValues("config.timeout.active");

  return (
    <div className="flex w-full flex-col gap-y-4">
      <div data-loc="ai-node-config-timeout">
        <div className="items-top flex justify-between gap-x-2">
          <div>
            <h3 className="text-gray-800 font-inter-semibold-13px">
              Use custom request timeout
            </h3>
            <span className="text-gray-500 font-inter-normal-12px">
              Enter the maximum number of seconds before timing out the request
            </span>
          </div>
          <Controller
            control={control}
            name="config.timeout.active"
            render={({ field: controlledField }) => (
              <Switch
                dataLoc="ai-node-config-timeout-toggle"
                disabled={immutable}
                enabled={controlledField.value}
                onChange={() => {
                  controlledField.onChange(!controlledField.value);
                }}
              />
            )}
          />
        </div>
        {isTimeoutActive && (
          <div className="flex w-full flex-row items-center justify-between gap-x-2 rounded-lg bg-gray-50 px-5 py-4">
            <div className="text-gray-700 font-inter-normal-12px">
              Time out after (seconds)
            </div>
            <Input
              data-loc="ai-node-config-timeout-timeout-seconds"
              disabled={immutable}
              placeholder="Enter timeout in seconds"
              type="number"
              {...register("config.timeout.timeout_seconds", {
                valueAsNumber: true,
                min: 1,
              })}
            />
          </div>
        )}
      </div>
      <div data-loc="ai-node-config-skip-on-error">
        <div className="items-top flex justify-between gap-x-2">
          <div>
            <h3 className="text-gray-800 font-inter-semibold-13px">
              Skip AI node on error
            </h3>
            <span className="text-gray-500 font-inter-normal-12px">
              In case of AI node errors such as hitting your quota limit, the
              flow will continue executing with all AI Node response fields set
              to `None`
            </span>
          </div>
          <Controller
            control={control}
            name="config.error.allow_errors"
            render={({ field: controlledField }) => (
              <Switch
                dataLoc="ai-node-config-skip-on-error-toggle"
                disabled={immutable}
                enabled={controlledField.value}
                onChange={() => {
                  controlledField.onChange(!controlledField.value);
                }}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export const AdvancedConfigV2: React.FC<AdvancedConfigPropsT> = ({
  immutable,
}) => {
  const formProps = useFormContext<AiNodeV2Form>();
  const responseType = formProps.getValues("inference.response_format.type");

  const isOutputStructured =
    responseType === AiNodeV2ResponseFormatConfigTypeEnum.JSON;

  return (
    <div className="flex w-full flex-col gap-y-4">
      <InferenceConfig
        immutable={immutable}
        isOutputStructured={isOutputStructured}
      />
      <Divider spacing="my-2" />
      <EnvConfig immutable={immutable} />
      <Divider spacing="my-2" />
      <ExecutionConfig immutable={immutable} />
    </div>
  );
};
